// src/lib/panneau-menage-api.js — LES DONNÉES PROPRES AU PANNEAU DU MÉNAGE.
//
// Deux briques, rien d'autre :
//   · chargerControleurDefauts — le DÉFAUT de contrôleur par logement
//     (table `menage_controle_defauts`, lisible en RLS directe par l'admin et
//     l'orchestrateur). BATCH au niveau vue (.in sur les logements affichés),
//     BEST-EFFORT : toute erreur rend {} — le panneau montre alors la
//     désignation par-ménage seule, jamais un écran cassé.
//   · designerControleurRun — la désignation PAR-MÉNAGE via l'action serveur
//     EXISTANTE `controle_designer` (POST /api/beds24-mapping, admin +
//     orchestrateur clampé serveur — voir lib/controles-handlers.js::
//     handleControleDesigner). Le panneau ne désigne QUE par-ménage ici.
//   · designerControleurLogement (2026-08-21) — le DÉFAUT du logement (la case
//     « contrôleur habituel » du menu, et la ligne « Qui contrôle quoi par
//     défaut » du panneau Planning) — même action serveur, portée logement.
//
// SYNCHRO (règle Dimitri 2026-08-21) : toute écriture de désignation émet
// l'événement `fbz:controle-designation` — les trois chemins (menu de l'écran
// Contrôle, ligne du panneau Planning, bloc du panneau de la frise) se
// rechargent en l'entendant : un changement fait ici se voit partout.

import { supabase } from '../supabase'

/**
 * logementIds → { [logement_id]: controleur_email } (défauts renseignés
 * seulement — un logement sans défaut n'a pas de clé).
 */
export async function chargerControleurDefauts(sb, logementIds) {
  const ids = [...new Set((logementIds || []).filter(Boolean))]
  if (!ids.length) return {}
  try {
    const { data, error } = await sb
      .from('menage_controle_defauts')
      .select('logement_id, controleur_email')
      .in('logement_id', ids)
    if (error) {
      console.error('[panneau] controleur defauts', error.message || error)
      return {}
    }
    const par = {}
    for (const d of data || []) {
      if (d?.logement_id && d?.controleur_email) par[d.logement_id] = d.controleur_email
    }
    return par
  } catch (e) {
    console.error('[panneau] controleur defauts', e?.message || e)
    return {}
  }
}

/**
 * Désignation PAR-MÉNAGE (run) — { runId, controleurEmail } ; controleurEmail
 * null RETIRE la désignation par-ménage (le défaut du logement reprend).
 * Rend { ok: true } ou { ok: false, erreur } — jamais de throw vers la vue.
 */
export async function designerControleurRun({ runId, controleurEmail }) {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) return { ok: false, erreur: 'Session expirée' }
    const res = await fetch('/api/beds24-mapping', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        action: 'controle_designer',
        run_id: runId,
        controleur_email: controleurEmail ?? null,
      }),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok) return { ok: false, erreur: json?.error || `Erreur ${res.status}` }
    window.dispatchEvent(new Event('fbz:controle-designation'))
    return { ok: true }
  } catch (e) {
    console.error('[panneau] controle_designer', e?.message || e)
    return { ok: false, erreur: 'Connexion impossible. Réessayez.' }
  }
}


/** Le DÉFAUT du logement — { logementId, controleurEmail|null (retire) }. */
export async function designerControleurLogement({ logementId, controleurEmail }) {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) return { ok: false, erreur: 'Session expirée — reconnectez-vous.' }
    const res = await fetch('/api/beds24-mapping', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ action: 'controle_designer', logement_id: logementId, controleur_email: controleurEmail }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      return { ok: false, erreur: body.error || `HTTP ${res.status}` }
    }
    window.dispatchEvent(new Event('fbz:controle-designation'))
    return { ok: true }
  } catch (e) {
    return { ok: false, erreur: e?.message || String(e) }
  }
}
